<template>
  <div class="ra-multi-select">
    <div class="ra-multi-select__container">
      <label
        v-if="label"
        :class="['ra-multi-select__label', labelClasses]"
        :for="`${internalId}-input`"
      >
        {{ label }}
      </label>
      <div class="ra-multi-select__input" @mousedown="handleInputContainerClick">
        <ul
          v-if="selectedItems.length > 0"
          class="ra-multi-select__tags"
          :aria-label="selectedItemsAccessibleLabel"
        >
          <li v-for="(item, index) in selectedItems" :key="index" class="ra-multi-select__tag">
            <span class="ra-multi-select__tag-text">{{ getItemLabel(item) }}</span>
            <span
              tabindex="0"
              role="button"
              class="ra-multi-select__tag-remove"
              :aria-label="getRemoveItemAriaLabel(item)"
              @click.stop="removeItem(index)"
              @keydown.space.prevent="removeItem(index)"
              @keydown.enter="removeItem(index)"
              @mousedown.stop
            >
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.9998 6.94043L1.78887 0.729492L0.729492 1.78887L6.94043 7.9998L0.729492 14.2107L1.78887 15.2701L7.9998 9.05918L14.2107 15.2701L15.2701 14.2107L9.05918 7.9998L15.2701 1.78887L14.2107 0.729492L7.9998 6.94043Z"
                />
              </svg>
            </span>
          </li>
        </ul>
        <input
          :id="`${internalId}-input`"
          ref="input"
          type="text"
          role="combobox"
          readonly
          class="ra-multi-select__field"
          :placeholder="placeholder"
          :aria-expanded="String(showDropdown)"
          aria-haspopup="listbox"
          :aria-controls="`${internalId}-options`"
          :aria-activedescendant="
            showDropdown && focusedOptionIndex !== -1
              ? `${internalId}-option-${focusedOptionIndex}`
              : null
          "
          :aria-invalid="String(!!errorMessage)"
          :aria-describedby="!!errorMessage ? `${internalId}-error-message` : null"
          @keydown="handleInputKeydown"
          @blur="showDropdown = false"
        />
      </div>

      <div v-if="showDropdown" class="ra-multi-select__dropdown">
        <div
          :id="`${internalId}-options`"
          ref="optionsContainer"
          class="ra-multi-select__options"
          role="listbox"
          aria-multiselectable="true"
          :aria-labelledby="`${internalId}-label`"
        >
          <div
            v-for="(item, index) in computedOptions"
            :id="`${internalId}-option-${index}`"
            :key="index"
            role="option"
            :aria-selected="String(isSelected(item))"
            :class="[
              'ra-multi-select__option',
              {
                'ra-multi-select__option--selected': isSelected(item),
                'ra-multi-select__option--focused': focusedOptionIndex === index,
              },
            ]"
            @mouseover="focusedOptionIndex = index"
            @mousedown.prevent
            @click="toggleItemSelection(item, index)"
          >
            <div class="ra-multi-select__option-button">
              <span class="ra-multi-select__option-label">{{ getItemLabel(item) }}</span>
              <div
                v-if="isSelected(item)"
                class="ra-multi-select__option-checkmark"
                aria-hidden="true"
              >
                <check-icon />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="errorMessage"
      :id="`${internalId}-error-message`"
      class="ra-multi-select__error-message"
      :class="errorMessageClasses"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
const INPUT_CHANGE_EVENT = 'input-changed';
import { common as commonProps } from './prop-definitions.js';
import CheckIcon from '../icons/check-icon.vue';

export default {
  name: 'refapp-multi-select',
  components: {
    CheckIcon,
  },
  props: {
    ...commonProps,
  },
  emits: [INPUT_CHANGE_EVENT],
  data() {
    return {
      showDropdown: false,
      selectedItems: [...this.value],
      focusedOptionIndex: -1,
      internalId: null,
    };
  },
  computed: {
    rawSelectedItems() {
      return this.selectedItems.map((item) => {
        return item[this.itemValue] || item;
      });
    },
    computedOptions() {
      return this.keyValueFormat ? Object.keys(this.options) : [...this.options];
    },
  },
  watch: {
    value(newVal) {
      this.selectedItems = [...newVal];
    },
  },
  created() {
    this.internalId = crypto.randomUUID();
  },
  methods: {
    handleInputContainerClick() {
      if (this.showDropdown) {
        this.showDropdown = false;
        return;
      }

      setTimeout(() => {
        this.$refs.input.focus();
        this.openDropdown();
      });
    },
    openDropdown() {
      this.showDropdown = true;
      this.focusedOptionIndex = 0;
    },
    handleInputKeydown(e) {
      if (!this.showDropdown) {
        if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
          e.preventDefault();
          this.openDropdown();
        }
        return;
      }

      const navigationKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
      const selectionKeys = ['Enter', ' '];

      const keyHandlers = {
        Escape: () => (this.showDropdown = false),
        ArrowDown: () => this.focusNextOption(),
        ArrowUp: () => this.focusPreviousOption(),
        Home: () => (this.focusedOptionIndex = 0),
        End: () => (this.focusedOptionIndex = this.computedOptions.length - 1),
      };

      keyHandlers[e.key]?.();

      if (selectionKeys.includes(e.key) && this.focusedOptionIndex !== -1) {
        this.toggleItemSelection(
          this.computedOptions[this.focusedOptionIndex],
          this.focusedOptionIndex,
        );
      }

      if (navigationKeys.includes(e.key) || selectionKeys.includes(e.key)) {
        e.preventDefault();
        this.scrollToFocusedOption();
      }
    },
    focusNextOption() {
      if (this.focusedOptionIndex < this.computedOptions.length - 1) {
        this.focusedOptionIndex++;
      } else {
        this.focusedOptionIndex = 0;
      }
    },
    focusPreviousOption() {
      if (this.focusedOptionIndex > 0) {
        this.focusedOptionIndex--;
      } else {
        this.focusedOptionIndex = this.computedOptions.length - 1;
      }
    },
    async scrollToFocusedOption() {
      await this.$nextTick();

      const optionsContainer = this.$refs.optionsContainer;
      const focusedOption = document.getElementById(
        `${this.internalId}-option-${this.focusedOptionIndex}`,
      );

      if (!optionsContainer || !focusedOption) return;

      const containerRect = optionsContainer.getBoundingClientRect();
      const optionRect = focusedOption.getBoundingClientRect();

      const isAboveViewport = optionRect.top < containerRect.top;
      const isBelowViewport = optionRect.bottom > containerRect.bottom;

      if (isAboveViewport) {
        optionsContainer.scrollTop = focusedOption.offsetTop - optionsContainer.offsetTop;
      } else if (isBelowViewport) {
        optionsContainer.scrollTop =
          focusedOption.offsetTop -
          optionsContainer.offsetTop -
          (optionsContainer.clientHeight - focusedOption.offsetHeight);
      }
    },
    toggleItemSelection(item, index) {
      this.focusedOptionIndex = index;
      const selectedItemIndex = this.selectedItems.findIndex(
        (selected) => this.getItemValue(selected) === this.getItemValue(item),
      );
      if (selectedItemIndex === -1) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems.splice(selectedItemIndex, 1);
      }
      this.confirmSelection();
    },
    isSelected(item) {
      return this.selectedItems.some(
        (selected) => this.getItemValue(selected) === this.getItemValue(item),
      );
    },
    confirmSelection() {
      this.$emit(INPUT_CHANGE_EVENT, this.rawSelectedItems);
    },
    removeItem(index) {
      this.showDropdown = false;
      this.selectedItems.splice(index, 1);
      this.$emit(INPUT_CHANGE_EVENT, this.rawSelectedItems);
    },
    getItemLabel(item) {
      if (this.keyValueFormat) {
        return typeof item === 'string' ? this.options[item] : item;
      }
      return typeof item === 'object' ? item[this.itemLabel] : item;
    },
    getItemValue(item) {
      if (this.keyValueFormat) {
        return item;
      }
      return typeof item === 'object' ? item[this.itemValue] : item;
    },
    getRemoveItemAriaLabel(item) {
      return this.removeItemAccessibleLabel?.replace('{{item}}', this.getItemLabel(item));
    },
  },
};
</script>

<style lang="scss">
@use './styles/style';
</style>
